import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

export type Plan = 'free' | 'starter' | 'pro' | 'enterprise';

interface TenantState {
  tenantId: string | null;
  tenantName: string | null;
  plan: Plan | null;
  /** PLAT-FF-1 — the tenant's evaluated feature flags, kept fresh by FeatureFlagsSync. */
  featureFlags: Record<string, boolean>;
  /** False until FeatureFlagsSync has loaded this tenant's flags; gated pages wait rather than refuse. */
  featureFlagsLoaded: boolean;
  setTenant: (params: SetTenantParams) => void;
  setFeatureFlags: (flags: Record<string, boolean>) => void;
  clearTenant: () => void;
}

export interface SetTenantParams {
  tenantId: string;
  tenantName: string;
  plan: Plan;
  featureFlags?: Record<string, boolean>;
}

/**
 * Dedicated tenant store, separate from auth state, so org-switching can
 * update the active tenant without tearing down the entire auth session.
 */
export const useTenantStore = create<TenantState>()(
  devtools(
    (set) => ({
      tenantId: null,
      tenantName: null,
      plan: null,
      featureFlags: {},
      featureFlagsLoaded: false,

      setTenant: (params) =>
        set(
          {
            tenantId: params.tenantId,
            tenantName: params.tenantName,
            plan: params.plan,
            // Another tenant's flags must not linger: every flag reads as off
            // until FeatureFlagsSync loads the new tenant's.
            featureFlags: params.featureFlags ?? {},
            featureFlagsLoaded: false,
          },
          false,
          'tenant/setTenant',
        ),

      setFeatureFlags: (featureFlags) =>
        set({ featureFlags, featureFlagsLoaded: true }, false, 'tenant/setFeatureFlags'),

      clearTenant: () =>
        set(
          {
            tenantId: null,
            tenantName: null,
            plan: null,
            featureFlags: {},
            featureFlagsLoaded: false,
          },
          false,
          'tenant/clearTenant',
        ),
    }),
    { name: 'TenantStore', enabled: import.meta.env.DEV },
  ),
);

// Typed selector hooks
export const useTenantId = () => useTenantStore((s) => s.tenantId);
export const useTenantName = () => useTenantStore((s) => s.tenantName);
export const useTenantPlan = () => useTenantStore((s) => s.plan);
export const useFeatureFlags = () => useTenantStore((s) => s.featureFlags);
export const useFeatureFlag = (flag: string) =>
  useTenantStore((s) => s.featureFlags[flag] ?? false);
export const useFeatureFlagsLoaded = () => useTenantStore((s) => s.featureFlagsLoaded);
export const useTenantActions = () =>
  useTenantStore(useShallow((s) => ({ setTenant: s.setTenant, clearTenant: s.clearTenant })));
